interface RetryOptions {
  maxRetries?: number;
  delay?: number;
  backoffMultiplier?: number;
  retryCondition?: (error: Error) => boolean;
}

const defaultRetryCondition = (error: Error): boolean => {
  // Retry on network errors and 5xx server errors
  return (
    error instanceof TypeError || // Network errors (CORS, DNS, etc.)
    error.message.includes('NetworkError') ||
    error.message.includes('Failed to fetch') ||
    (error.message.includes('HTTP') && !!error.message.match(/5\d\d/)) // 5xx errors
  );
};

export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryOptions: RetryOptions = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    delay = 1000,
    backoffMultiplier = 2,
    retryCondition = defaultRetryCondition,
  } = retryOptions;

  const attemptFetch = async (attemptsLeft: number): Promise<Response> => {
    try {
      const response = await fetch(url, options);

      // If response is ok or it's a client error (4xx), don't retry
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }

      // For server errors (5xx), check if we should retry
      const error = new Error(`HTTP ${response.status}`);
      if (attemptsLeft === 0 || !retryCondition(error)) {
        return response; // Return the response even if it's an error
      }

      // Wait and retry
      const waitTime =
        delay * Math.pow(backoffMultiplier, maxRetries - attemptsLeft);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return attemptFetch(attemptsLeft - 1);
    } catch (error) {
      const fetchError = error as Error;

      // If no more attempts or error shouldn't be retried, throw it
      if (attemptsLeft === 0 || !retryCondition(fetchError)) {
        throw fetchError;
      }

      // Wait and retry
      const waitTime =
        delay * Math.pow(backoffMultiplier, maxRetries - attemptsLeft);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      return attemptFetch(attemptsLeft - 1);
    }
  };

  return attemptFetch(maxRetries);
}
